import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { FormatConfigService } from './format-config.service';
import { AppConfigService } from './app-config.service';
import {
  BaseerweiterungService,
  BaseformatService,
  BaseimageService,
  BaseorderService,
  BaseparameterService,
  BasesettingService,
  ErweiterungConfigResponse,
  FormatConfigResponse,
  ImageConfigResponse,
  MenuConfigResponse,
  ParameterConfigResponse,
  SettingsConfigResponse
} from 'app/core/base-api';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { AppPrintConfig, CookiePrintConfig } from './app-print-config';
import { AppFormatConfig } from './app-format-config';
import { AppImageConfig } from './app-image-config';
import { AppAuszugCookieService } from './app-auszug-cookie-service';
import { environment } from 'environments/environment';
import { GbprintService, PrintConfigResponseMeta } from 'app/core/gb-api';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppFormatConfigService extends FormatConfigService {

  private _formatConfig = new BehaviorSubject<AppFormatConfig>(null);
  private _printConfig = new BehaviorSubject<AppPrintConfig>(null);
  private _settingsConfig = new BehaviorSubject<SettingsConfigResponse>(null);
  private _erweiterungConfig = new BehaviorSubject<ErweiterungConfigResponse>(null);
  private _parameterConfig = new BehaviorSubject<ParameterConfigResponse>(null);

  private _menuConfigSubject = new BehaviorSubject<MenuConfigResponse>(null);
  private _menuConfig$: Observable<MenuConfigResponse> = this._menuConfigSubject.asObservable();

  private _imageConfigSubject = new BehaviorSubject<AppImageConfig>(null);
  private _imageConfig$: Observable<AppImageConfig> = this._imageConfigSubject.asObservable();

  private _initializedSubject = new BehaviorSubject<boolean>(false);
  private _initialized$: Observable<boolean> = this._initializedSubject.asObservable();

  private _deploymentMode = environment.deploymentMode;

  constructor(private appConfigService: AppConfigService,
              private imageService: BaseimageService,
              private settingService: BasesettingService,
              private formatService: BaseformatService,
              private menuService: BaseorderService,
              private translateService: TranslateService,
              private erweiterungService: BaseerweiterungService,
              private parameterService: BaseparameterService,
              private gbPrintService: GbprintService,
              private appAuszugCookieService: AppAuszugCookieService,
              private injector: Injector) {
    super();
  }

  // @ts-ignore
  init(): Promise<any> {
    this.injector.get(OAuthService).events
      .pipe(
        filter(data => data.type === 'token_received'),
        first()
      )
      .subscribe(() => {
        this.configsLoaded(() => undefined);
      });
    return new Promise<any>((resolve) => {
      this.configsLoaded(resolve);
    });
  }

  configsLoaded(resolve) {
    const imageConfig$ = this.imageService.getImageConfig(this.appConfigService.kanton);
    const formatConfig$ = this.formatService.getFormatConfig(this.appConfigService.kanton);
    const settingsConfig$ = this.settingService.getSettingsConfig();
    const menuConfig$ = this.menuService.getMenuConfig(this.appConfigService.kanton, this.getDeploymentMode());
    const erweiterungConfig$ = this.erweiterungService.getActiveErweiterungen();
    const parameterConfig$ = this.parameterService.getAllParameter();
    const printConfigResponse$ = this.gbPrintService.getPrintOptions();

    forkJoin([imageConfig$, formatConfig$, settingsConfig$, erweiterungConfig$, parameterConfig$, menuConfig$, printConfigResponse$]).subscribe({
      next: (results) => {
        this._formatConfig.next(new AppFormatConfig(<FormatConfigResponse>results[1]));
        this._settingsConfig.next(<SettingsConfigResponse>results[2]);
        this._erweiterungConfig.next(<ErweiterungConfigResponse>results[3]);
        this._parameterConfig.next(<ParameterConfigResponse>results[4]);
        this._imageConfigSubject.next(new AppImageConfig(<ImageConfigResponse>results[0], this.translateService));
        this._menuConfigSubject.next(<MenuConfigResponse>results[5]);
        const cookiePrintConfig = new CookiePrintConfig(<PrintConfigResponseMeta>results[6], this.appAuszugCookieService);
        cookiePrintConfig.init().then(() => {
          this._printConfig.next(cookiePrintConfig);
        });
        this._initializedSubject.next(true);
        resolve();
      }, error: () => resolve(false)
    });
  }

  private getDeploymentMode() {
    return this._deploymentMode === 'PUBLIC' ? 'PUBLIC' : 'MENU';
  }

  get menuConfig$(): Observable<MenuConfigResponse> {
    return this._menuConfig$;
  }

  get imageConfig$(): Observable<AppImageConfig> {
    return this._imageConfig$;
  }

  get initialized$(): Observable<boolean> {
    return this._initialized$;
  }

  get settingsConfig$(): Observable<SettingsConfigResponse> {
    return this._settingsConfig.asObservable();
  }

  get erweiterungsConfig$(): Observable<ErweiterungConfigResponse> {
    return this._erweiterungConfig.asObservable();
  }

  get parameterConfig$(): Observable<ParameterConfigResponse> {
    return this._parameterConfig.asObservable();
  }

  get formatConfig$(): Observable<AppFormatConfig> {
    return this._formatConfig.asObservable();
  }

  get printConfig$(): Observable<AppPrintConfig> {
    return this._printConfig.asObservable();
  }


}
